
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, FileText } from "lucide-react";
import { FluxoCard, KanbanColumn } from "@/components/solar/kanban/types";

interface CardListProps {
  columns: KanbanColumn[];
  onEditClick: (card: FluxoCard) => void;
  onViewProjeto: (card: FluxoCard) => void;
  formatDate: (date: Date) => string;
}

export const CardList = ({
  columns,
  onEditClick,
  onViewProjeto,
  formatDate
}: CardListProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lista de Projetos</CardTitle>
        <CardDescription>Visualize todos os projetos em uma lista</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative w-full overflow-auto">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                <th className="h-12 px-4 text-left align-middle font-medium">Título</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Cliente</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Status</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Prioridade</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Data</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Progresso</th>
                <th className="h-12 px-4 text-left align-middle font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {columns.flatMap(column => column.cards).map((card) => {
                const checklist = card.checklistPreenchido || {
                  integrador: false,
                  unidadeConsumidora: false,
                  eletricos: false,
                  mecanicos: false,
                  financeiros: false,
                  documentos: false
                };
                
                const totalItems = Object.keys(checklist).length;
                const completedItems = Object.values(checklist).filter(Boolean).length;
                const progressPercentage = Math.round((completedItems / totalItems) * 100);
                
                return (
                  <tr key={card.id} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-4 align-middle">{card.titulo}</td>
                    <td className="p-4 align-middle">{card.cliente || '-'}</td>
                    <td className="p-4 align-middle">
                      <Badge variant={
                        card.status === "fechado" 
                          ? "success" 
                          : card.status === "perdido"
                          ? "destructive"
                          : "outline"
                      }>
                        {
                          card.status === "lead" ? "Lead" :
                          card.status === "contato" ? "Em Contato" :
                          card.status === "proposta" ? "Proposta Enviada" :
                          card.status === "negociacao" ? "Em Negociação" :
                          card.status === "fechado" ? "Fechado" :
                          "Perdido"
                        }
                      </Badge>
                    </td>
                    <td className="p-4 align-middle">
                      <Badge variant={
                        card.prioridade === "alta" 
                          ? "destructive" 
                          : card.prioridade === "media"
                          ? "warning"
                          : "success"
                      }>
                        {
                          card.prioridade === "alta" ? "Alta" :
                          card.prioridade === "media" ? "Média" :
                          "Baixa"
                        }
                      </Badge>
                    </td>
                    <td className="p-4 align-middle">{formatDate(card.criadoEm)}</td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              progressPercentage >= 66 
                                ? "bg-green-500" 
                                : progressPercentage >= 33
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <span className="text-xs whitespace-nowrap">{progressPercentage}%</span>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onEditClick(card)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onViewProjeto(card)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              
              {columns.flatMap(column => column.cards).length === 0 && (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-muted-foreground">
                    Nenhum projeto cadastrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
